import { Box, Grid, Icon, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Assets from "../../Component/Common/Assets";
import SalesPurchaseChart from "./SalesPurchaseChart";
import Expense from "./Expense";
import POStatus from "./POStatus";
import POStatusTable from "./POStatusTable";
import VenderOverView from "./VenderOverView";
import useSnackBar from "../../Redux/hooks/useSnackBar";
import usePageLoader from "../../Redux/hooks/usePageLoader";
import { getAllRevenueAction } from "../../Redux/Actions/DashboardActions";
import ErrorHandler from "../../lib/errorHandler";

// ------------------ Dashboard Card ------------------
const DashboardCard = ({ title, count, change, icon, background }) => {
  const theme = useTheme();

  const changeValue =
    typeof change === "string" ? change : change.toFixed(2) + "%";
  const isIncrease = !changeValue.includes("-");

  return (
    <Box
      sx={{
        width: "100%",
        height: 180,
        borderRadius: "20px",
        background: "linear-gradient(294deg, #FFF 66.09%, #FEA181 243.86%)",
        padding: "20px",
        boxSizing: "border-box",
        position: "relative",
        border: "0.5px solid rgba(143, 143, 143, 0.30)",
        overflow: "hidden",
      }}
    >
      {/* Icon circle */}
      <Box
        sx={{
          height: "51px",
          width: "51px",
          borderRadius: "50%",
          background: "#E7784366",
          position: "absolute",
          top: "25px",
          right: "25px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Assets
          absolutePath={true}
          src={`/assets/img/${icon}`}
          height={30}
          width={30}
        />
      </Box>

      {/* Title & Count */}
      <Box display="flex" flexDirection="column" gap="5px">
        <Typography
          sx={{
            fontSize: "18px",
            color: theme.palette.bgBlack.secondary,
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
        <Typography fontWeight={900} fontSize="30px">
          {count}
        </Typography>
      </Box>

      {/* Change Value & Line */}
      <Box sx={{ display: "flex", marginTop: "20px", alignItems: "center" }}>
        <Typography fontWeight={700} fontSize="16px">
          {changeValue}
        </Typography>

        {isIncrease ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              ml: 1,
            }}
          >
            <Icon>
              <Assets absolutePath={true} src="/assets/img/ArrowUp.svg" />
            </Icon>
            <Assets
              absolutePath={true}
              src="/assets/img/greeLine.svg"
              height={33}
              width={232}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              ml: 1,
            }}
          >
            <Icon>
              <Assets absolutePath={true} src="/assets/img/ArrowDown.svg" />
            </Icon>
            <Assets
              absolutePath={true}
              src="/assets/img/redLine.svg"
              height={33}
              width={232}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ------------------ Dashboard ------------------
const Dashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { setSnackBar } = useSnackBar();
  const setFullPageLoader = usePageLoader();

  // ---------- State ----------
  const [data, setData] = useState({
    wastedProducts: { currentMonth: 0, comparePercentage: 0 },
    totalProduction: { currentMonth: 0, comparePercentage: 0 },
    totalSales: { currentMonth: 0, comparePercentage: 0 },
    totalRevenue: { currentMonth: 0, comparePercentage: 0 },
  });

  const dashboardData = [
    {
      title: "Total Production",
      count: data?.totalProduction?.currentMonth || 0,
      change: data?.totalProduction?.comparePercentage || 0,
      icon: "Box.svg",
      background: theme.palette.primary.light,
    },
    {
      title: "Total Revenue",
      count: data?.totalRevenue?.currentMonth || 0,
      change: data?.totalRevenue?.comparePercentage || 0,
      icon: "MoneyBag.svg",
      background: theme.palette.lightYellow.light,
    },
    {
      title: "Total Sales",
      count: data.totalSales?.currentMonth || 0,
      change: data.totalSales?.comparePercentage || 0,
      icon: "TagIcon.svg",
      background: theme.palette.lightTomato.main,
    },
    {
      title: "Wasted Products",
      count: data.wastedProducts?.currentMonth || 0,
      change: data.wastedProducts?.comparePercentage || 0,
      icon: "Box.svg",
      background: theme.palette.lightYellow.main,
    },
  ];

  // ---------- Handlers ----------
  const getAllRevenueData = async () => {
    setFullPageLoader(true);

    try {
      const res = await dispatch(getAllRevenueAction());
      const ok = ErrorHandler(res, setSnackBar);

      if (ok) {
        setData(res.payload.data.response);
      }
    } catch (err) {
      setSnackBar("error", err.message);
    } finally {
      setFullPageLoader(false);
    }
  };

  // ---------- Effects ----------
  useEffect(() => {
    getAllRevenueData();
  }, []);

  // ---------- Render ----------
  return (
    <Box p={3}>
      {/* Top Cards */}
      <Grid container spacing={3}>
        {dashboardData.map((item, index) => (
          <Grid key={index} item xs={12} sm={6} md={6} lg={3}>
            <DashboardCard {...item} />
          </Grid>
        ))}
      </Grid>

      {/* Sales & Expense */}
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={6}>
            <SalesPurchaseChart />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Expense />
          </Grid>
        </Grid>
      </Box>

      {/* Status & Overview */}
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={4}>
            <POStatus />
          </Grid>
          <Grid item xs={12} md={12} lg={4}>
            <POStatusTable />
          </Grid>
          <Grid item xs={12} md={12} lg={4}>
            <VenderOverView />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
